import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { requireConsentSession, requireSetupOpen, requireSignedOut } from './guards.ts';
import { rootRoute } from './root.tsx';

const text = (value: unknown) => (typeof value === 'string' && value ? value : undefined);

const REASONS = ['expired', 'signed-out'] as const;

export interface LoginSearch {
  redirect?: string;
  sso_error?: string;
  method?: 'password';
  reason?: (typeof REASONS)[number];
}

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'login',
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: text(search['redirect']),
    // Set by a single sign-on that did not finish; the page explains the reason.
    sso_error: text(search['sso_error']),
    // The admin fallback's password form while single sign-on is required.
    method: search['method'] === 'password' ? 'password' : undefined,
    // Why the person is here again: their session ended, or they signed out.
    reason: REASONS.find((reason) => reason === search['reason']),
  }),
  beforeLoad: ({ context, search }) =>
    requireSignedOut(context.queryClient, search.redirect ?? '/'),
  component: lazyRouteComponent(() => import('../pages/auth/login-page.tsx'), 'LoginPage'),
});

export const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'forgot-password',
  // Carried over from the sign-in form, so nobody types their address twice.
  validateSearch: (search: Record<string, unknown>): { email?: string } => ({
    email: text(search['email']),
  }),
  component: lazyRouteComponent(
    () => import('../pages/auth/forgot-password-page.tsx'),
    'ForgotPasswordPage',
  ),
});

/** Email links carry the token in the fragment (#token=…), so it never reaches a server log. */
export const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'reset-password',
  component: lazyRouteComponent(
    () => import('../pages/auth/reset-password-page.tsx'),
    'ResetPasswordPage',
  ),
});

export const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'accept-invitation',
  component: lazyRouteComponent(
    () => import('../pages/auth/accept-invitation-page.tsx'),
    'AcceptInvitationPage',
  ),
});

/** The per-kind unsubscribe link in every notification email; works without signing in. */
export const unsubscribeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'unsubscribe',
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: text(search['token']),
  }),
  component: lazyRouteComponent(
    () => import('../pages/auth/unsubscribe-page.tsx'),
    'UnsubscribePage',
  ),
});

/**
 * Where an MCP client sends the browser to sign in (the authorization endpoint). The page reads
 * the request from the raw query; the server checks it again on every call.
 */
export const oauthAuthorizeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'oauth/authorize',
  beforeLoad: ({ context, location }) => requireConsentSession(context.queryClient, location.href),
  component: lazyRouteComponent(
    () => import('../pages/auth/oauth-consent-page.tsx'),
    'OAuthConsentPage',
  ),
});

/**
 * Where a command line tool (bp) sends the person to approve its sign-in (RFC 8628). Needs a
 * signed-in person like the consent screen; `code` fills the field from the tool's link.
 */
export const deviceSignInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'device',
  validateSearch: (search: Record<string, unknown>): { code?: string } => ({
    code: text(search['code']),
  }),
  beforeLoad: ({ context, location }) => requireConsentSession(context.queryClient, location.href),
  component: lazyRouteComponent(
    () => import('../pages/auth/device-sign-in-page.tsx'),
    'DeviceSignInPage',
  ),
});

const STEPS = [1, 2, 3, 4, 5, 6, 7] as const;

export interface SetupSearch {
  step?: number;
  /** The first sign-in of an admin whose setup is unfinished: the welcome comes first. */
  welcome?: boolean;
}

export const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'setup',
  validateSearch: (search: Record<string, unknown>): SetupSearch => {
    const step = Number(search['step']);
    return {
      step: STEPS.includes(step as (typeof STEPS)[number]) ? step : undefined,
      welcome: search['welcome'] === true || search['welcome'] === 'true' ? true : undefined,
    };
  },
  beforeLoad: ({ context }) => requireSetupOpen(context.queryClient),
  component: lazyRouteComponent(() => import('../pages/setup/setup-page.tsx'), 'SetupPage'),
});

export const publicRoutes = [
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  unsubscribeRoute,
  invitationRoute,
  setupRoute,
  oauthAuthorizeRoute,
  deviceSignInRoute,
];
